import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { HiOutlineInformationCircle } from "react-icons/hi";

import DateTimePicker from "components/DateTimePicker";
import radioCheck from "assets/svg/radio-check.svg";
import { formatTmNoForDisplay } from "utils/tmNumberDisplay";
import {
  generateOppositionEvidenceByCpPresignedUrls,
  getOppositionEvidenceByConflictingProprietor,
  saveOppositionEvidenceByConflictingProprietor,
  type OppositionEvidenceByCpItem,
} from "api/opposition";
import { downloadFileFromUrlWithFallback } from "utils/downloadFileFromUrl";

export const OPPOSITION_STATUS_DETAILS_FOR_EVIDENCE_BY_CP_FORM_ID =
  "opposition-status-details-for-evidence-by-cp-form";

const LABEL_CLASS = "text-[12px] font-normal leading-5 text-[#222222]";
const READONLY_CLASS =
  "h-9 min-h-[44px] md:min-h-0 w-full rounded-[6px] border border-[#EEEEEE] bg-[#F7F7F7] px-2.5 py-2 text-[12px] leading-5 text-[#6D6D6D] box-border";

function InfoTooltip({ text }: { text: string }) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLSpanElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <span
      ref={containerRef}
      className="relative inline-flex items-center"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <button
        type="button"
        aria-label={text}
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        className="inline-flex items-center justify-center w-4 h-4 text-[#6D6D6D] hover:text-[#222] focus:outline-none focus:ring-1 focus:ring-[#222] rounded-full"
      >
        <HiOutlineInformationCircle className="w-4 h-4" strokeWidth={1.75} />
      </button>
      {open && (
        <span
          role="tooltip"
          className="absolute left-5 top-0 z-30 w-[260px] rounded-[8px] border border-[#EEEEEE] bg-white p-2.5 text-[11px] leading-4 text-[#222] shadow-lg"
        >
          {text}
        </span>
      )}
    </span>
  );
}

function RadioCircle({ active }: { active: boolean }) {
  if (active) return <img src={radioCheck} alt="" width={14} height={14} />;
  return (
    <span
      aria-hidden
      className="inline-block w-[14px] h-[14px] rounded-full border border-[#C3C3C3] bg-white"
    />
  );
}

function parseISODate(iso: string | null | undefined): Date | null {
  if (!iso) return null;
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return null;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return isNaN(d.getTime()) ? null : d;
}

function toISODateString(date: Date | null): string | null {
  if (!date) return null;
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function YesNoToggle({
  value,
  onChange,
  disabled,
}: {
  value: boolean | null;
  onChange: (v: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <div className="flex items-center gap-2">
      {(
        [
          { v: true, l: "YES" },
          { v: false, l: "NO" },
        ] as const
      ).map(({ v, l }) => (
        <button
          key={l}
          type="button"
          disabled={disabled}
          onClick={() => onChange(v)}
          className={`flex items-center gap-1.5 px-4 py-2 rounded-[6px] text-xs font-medium transition-colors disabled:opacity-60 h-9 min-w-[88px] ${
            value === v
              ? "bg-[#222222] text-white"
              : "bg-white border border-[#EEEEEE] text-[#222222] hover:bg-[#F7F7F7]"
          }`}
        >
          <RadioCircle active={value === v} />
          {l}
        </button>
      ))}
    </div>
  );
}

type Row = {
  id: string;
  applicationNo: string | null;
  lastDateForEvidenceByCp: string | null;
  evidenceFiled: boolean | null;
  dateOfEvidence: Date | null;
  dateOfReceipt: Date | null;
  lastDateOfFilingRejoinder: Date | null;
  pdfFileKey: string | null;
  pdfFilename: string | null;
  pdfDownloadUrl: string | null;
  pendingFile: File | null;
  localPreviewUrl: string | null;
};

type Props = {
  leadId?: string | null;
  onSuccess?: (leadId: string) => void;
};

export default function OppositionStatusDetailsForEvidenceByCpView({
  leadId,
  onSuccess,
}: Props) {
  const [loading, setLoading] = useState<boolean>(!!leadId);
  const [saving, setSaving] = useState(false);
  const [rows, setRows] = useState<Row[]>([]);
  const [activeIdx, setActiveIdx] = useState(0);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (!leadId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    getOppositionEvidenceByConflictingProprietor(leadId)
      .then((data) => {
        if (cancelled) return;
        setRows(
          data.conflictingMarks.map(
            (m: OppositionEvidenceByCpItem) => ({
              id: m.id,
              applicationNo: m.applicationNo,
              lastDateForEvidenceByCp:
                m.lastDateForEvidenceByConflictingProprietor,
              evidenceFiled: m.evidenceFiledByConflictingProprietor,
              dateOfEvidence: parseISODate(
                m.dateOfEvidenceByConflictingProprietor
              ),
              dateOfReceipt: parseISODate(
                m.dateOfReceiptOfEvidenceFromConflictingProprietor
              ),
              lastDateOfFilingRejoinder: parseISODate(m.lastDateOfFilingRejoinder),
              pdfFileKey: m.evidenceFileKey,
              pdfFilename: m.evidenceFilename,
              pdfDownloadUrl: m.evidenceDownloadUrl,
              pendingFile: null,
              localPreviewUrl: null,
            })
          )
        );
        setActiveIdx(0);
      })
      .catch((err) => {
        if (!cancelled) {
          toast.error(
            err instanceof Error ? err.message : "Failed to load step data"
          );
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [leadId]);

  useEffect(
    () => () => {
      rows.forEach((r) => {
        if (r.localPreviewUrl) URL.revokeObjectURL(r.localPreviewUrl);
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const activeRow = rows[activeIdx] ?? null;

  const updateActive = (patch: Partial<Row>) => {
    if (!activeRow) return;
    setRows((prev) =>
      prev.map((r) => (r.id === activeRow.id ? { ...r, ...patch } : r))
    );
  };

  const handleDateOfReceiptChange = (d: Date | null) => {
    if (!activeRow) return;
    let nextLastDate: Date | null = null;
    if (d) {
      nextLastDate = new Date(d);
      nextLastDate.setMonth(nextLastDate.getMonth() + 1);
    }
    updateActive({ dateOfReceipt: d, lastDateOfFilingRejoinder: nextLastDate });
  };

  const handlePdfChosen = (file: File | null) => {
    if (!activeRow) return;
    if (activeRow.localPreviewUrl) URL.revokeObjectURL(activeRow.localPreviewUrl);
    if (!file) {
      updateActive({ pendingFile: null, localPreviewUrl: null });
      return;
    }
    updateActive({
      pendingFile: file,
      localPreviewUrl: URL.createObjectURL(file),
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadId || saving) return;
    setSaving(true);
    try {
      const uploads = rows
        .filter((r) => r.pendingFile)
        .map((r) => ({ rowId: r.id, file: r.pendingFile! }));
      const keyMap: Record<string, { key: string; filename: string }> = {};
      if (uploads.length > 0) {
        const presigned = await generateOppositionEvidenceByCpPresignedUrls(
          leadId,
          uploads.map((u) => ({
            filename: u.file.name,
            mimeType: u.file.type || "application/pdf",
          }))
        );
        if (presigned.length !== uploads.length) {
          throw new Error("Failed to generate upload URLs");
        }
        await Promise.all(
          uploads.map(async (u, i) => {
            const target = presigned[i];
            const res = await fetch(target.url, {
              method: "PUT",
              body: u.file,
              headers: {
                "Content-Type": u.file.type || "application/pdf",
              },
            });
            if (!res.ok) throw new Error(`Failed to upload ${u.file.name}`);
            keyMap[u.rowId] = { key: target.key, filename: u.file.name };
          })
        );
      }
      const saved = await saveOppositionEvidenceByConflictingProprietor(leadId, {
        conflictingMarks: rows.map((r) => {
          const upload = keyMap[r.id];
          return {
            conflictingMarkId: r.id,
            evidenceFiledByConflictingProprietor: r.evidenceFiled,
            dateOfEvidenceByConflictingProprietor: toISODateString(
              r.dateOfEvidence
            ),
            dateOfReceiptOfEvidenceFromConflictingProprietor: toISODateString(
              r.dateOfReceipt
            ),
            lastDateOfFilingRejoinder: toISODateString(
              r.lastDateOfFilingRejoinder
            ),
            evidenceFileKey: upload?.key ?? r.pdfFileKey,
            evidenceFilename: upload?.filename ?? r.pdfFilename,
          };
        }),
      });
      setRows(
        saved.conflictingMarks.map((m) => ({
          id: m.id,
          applicationNo: m.applicationNo,
          lastDateForEvidenceByCp: m.lastDateForEvidenceByConflictingProprietor,
          evidenceFiled: m.evidenceFiledByConflictingProprietor,
          dateOfEvidence: parseISODate(m.dateOfEvidenceByConflictingProprietor),
          dateOfReceipt: parseISODate(
            m.dateOfReceiptOfEvidenceFromConflictingProprietor
          ),
          lastDateOfFilingRejoinder: parseISODate(m.lastDateOfFilingRejoinder),
          pdfFileKey: m.evidenceFileKey,
          pdfFilename: m.evidenceFilename,
          pdfDownloadUrl: m.evidenceDownloadUrl,
          pendingFile: null,
          localPreviewUrl: null,
        }))
      );
      toast.success("Status details saved.");
      onSuccess?.(leadId);
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to save status details"
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDownload = async () => {
    if (!activeRow?.pdfDownloadUrl) return;
    try {
      const result = await downloadFileFromUrlWithFallback(
        activeRow.pdfDownloadUrl,
        activeRow.pdfFilename ?? "evidence-by-cp.pdf"
      );
      if (result === "local") toast.success("PDF saved to your device.");
      else toast.info("Opened the PDF in a new tab.");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Download failed");
    }
  };

  const handlePreview = () => {
    const url = activeRow?.localPreviewUrl ?? activeRow?.pdfDownloadUrl;
    if (!url) return;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  if (!leadId) {
    return (
      <div className="flex flex-col gap-[22px] w-full max-w-[1085px] py-12">
        <p className="text-sm text-[#6D6D6D]">
          Please complete the previous steps to access this step.
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex flex-col gap-[22px] w-full max-w-[1085px] py-12">
        <p className="text-sm text-[#6D6D6D]">Loading…</p>
      </div>
    );
  }

  if (rows.length === 0 || !activeRow) {
    return (
      <div className="flex flex-col gap-[22px] w-full max-w-[1085px] py-12">
        <p className="text-sm text-[#6D6D6D]">
          No applications on an ACTIVE Evidence invoice yet.
        </p>
      </div>
    );
  }

  return (
    <form
      id={OPPOSITION_STATUS_DETAILS_FOR_EVIDENCE_BY_CP_FORM_ID}
      onSubmit={handleSubmit}
      className="flex w-full max-w-[1085px] flex-col gap-5"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      {rows.length > 1 && (
        <div className="flex flex-row flex-wrap gap-3">
          {rows.map((r, idx) => {
            const isActive = idx === activeIdx;
            return (
              <button
                key={r.id}
                type="button"
                onClick={() => setActiveIdx(idx)}
                className={`flex items-center gap-2 px-2.5 py-2.5 rounded-[6px] h-10 min-w-[140px] transition-colors text-xs ${
                  isActive
                    ? "bg-[#222222] text-white"
                    : "bg-[#F7F7F7] text-[#222222]"
                }`}
              >
                <RadioCircle active={isActive} />
                {formatTmNoForDisplay(r.applicationNo ?? "")}
              </button>
            );
          })}
        </div>
      )}

      <YesNoToggle
        value={activeRow.evidenceFiled}
        onChange={(v) => updateActive({ evidenceFiled: v })}
        disabled={saving}
      />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
        <div className="flex flex-col gap-1">
          <label className={LABEL_CLASS}>Date of Evidence by Conflicting Proprietor</label>
          <DateTimePicker
            value={activeRow.dateOfEvidence}
            onChange={(d) => updateActive({ dateOfEvidence: d })}
            showTime={false}
            placeholder="Select Date"
            buttonClassName="!h-9 !min-h-0 !text-[12px] rounded-[6px]"
          />
        </div>
        <div className="flex flex-col gap-1">
          <label className={LABEL_CLASS}>
            Date of Receipt of Evidence from Conflicting Proprietor
          </label>
          <DateTimePicker
            value={activeRow.dateOfReceipt}
            onChange={handleDateOfReceiptChange}
            showTime={false}
            placeholder="Select Date"
            buttonClassName="!h-9 !min-h-0 !text-[12px] rounded-[6px]"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-1.5">
            <label className={LABEL_CLASS}>Last date of filing Rejoinder</label>
            <InfoTooltip text="Last date of filing Rejoinder is exactly 1 month from the Date of Receipt of Evidence from Conflicting Proprietor. You can edit this date if needed." />
          </div>
          <DateTimePicker
            value={activeRow.lastDateOfFilingRejoinder}
            onChange={(d) => updateActive({ lastDateOfFilingRejoinder: d })}
            showTime={false}
            placeholder="Auto (Date of Receipt + 1 month)"
            buttonClassName="!h-9 !min-h-0 !text-[12px] rounded-[6px]"
          />
        </div>
        <div className="flex flex-col gap-1">
          <label className={LABEL_CLASS}>
            Last Date for Evidence by Conflicting Proprietor
          </label>
          <input
            type="text"
            value={
              activeRow.lastDateForEvidenceByCp
                ? activeRow.lastDateForEvidenceByCp.replace(
                    /^(\d{4})-(\d{2})-(\d{2}).*/,
                    "$3-$2-$1"
                  )
                : ""
            }
            placeholder="Set in File Evidence Affidavit (Step 15)"
            readOnly
            className={READONLY_CLASS}
          />
        </div>
      </div>

      <div className="flex flex-col gap-2">
        <label className={LABEL_CLASS}>
          Evidence PDF from Conflicting Proprietor
        </label>
        <div className="flex flex-col sm:flex-row flex-wrap gap-2.5">
          <input
            ref={fileInputRef}
            type="file"
            accept="application/pdf"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0] ?? null;
              handlePdfChosen(f);
              if (fileInputRef.current) fileInputRef.current.value = "";
            }}
          />
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={saving}
            className="inline-flex items-center justify-center h-9 px-4 text-[12px] font-medium leading-5 text-[#222] bg-white border border-[#EEEEEE] rounded-[6px] hover:bg-[#F7F7F7] disabled:opacity-60"
          >
            {activeRow.pendingFile || activeRow.pdfFilename
              ? "Replace PDF"
              : "Upload PDF"}
          </button>
          {(activeRow.pendingFile || activeRow.pdfDownloadUrl) && (
            <button
              type="button"
              onClick={handlePreview}
              disabled={saving}
              className="inline-flex items-center justify-center h-9 px-4 text-[12px] font-medium leading-5 text-[#222] bg-white border border-[#EEEEEE] rounded-[6px] hover:bg-[#F7F7F7] disabled:opacity-60"
            >
              Preview
            </button>
          )}
          {activeRow.pdfDownloadUrl && (
            <button
              type="button"
              onClick={handleDownload}
              disabled={saving}
              className="inline-flex items-center justify-center h-9 px-4 text-[12px] font-medium leading-5 text-[#222] bg-white border border-[#EEEEEE] rounded-[6px] hover:bg-[#F7F7F7] disabled:opacity-60"
            >
              Download
            </button>
          )}
        </div>
        {(activeRow.pendingFile || activeRow.pdfFilename) && (
          <span className="text-[11px] leading-4 text-[#6D6D6D]">
            {activeRow.pendingFile
              ? `New: ${activeRow.pendingFile.name}`
              : activeRow.pdfFilename}
          </span>
        )}
      </div>
    </form>
  );
}
